import {
  BaseError,
  ContractFunctionRevertedError,
  createPublicClient,
  createWalletClient,
  decodeErrorResult,
  encodeFunctionData,
  getAddress,
  http,
  parseEventLogs,
  type Account,
  type Address,
  type Chain,
  type Hex,
  type PublicClient,
  type Transport,
  type WalletClient,
} from "viem";
import type { Intent } from "@woodpecker/protocol";
import {
  serviceAuthorizationAbi,
  serviceDirectoryAbi,
  serviceTokenAbi,
  serviceTokenBytecode,
  wpCreditAbi,
  extensionDirectoryAbi,
} from "./artifacts";
import {
  ChainError,
  type Authorization,
  type ChainAddresses,
  type ChainErrorName,
  type ChainWriter,
  type DirectoryEntry,
  type ExchangedEvent,
  type ScanOptions,
  type SettleResult,
  type SpentEvent,
  type TokenInfo,
  type Tx,
  type ExtensionEntry,
} from "./types";
import type { TransactionReceipt } from "viem";

export interface ViemChainConfig {
  rpcUrl: string;
  addresses: ChainAddresses;
  /** The one account this client sends from (the wallet, a submitter, Woodpecker). Reads need none. */
  account?: Account;
  /** For a browser: a viem transport instead of http(rpcUrl). */
  transport?: Transport;
  /** The first block to scan for events (the deployment block of contract A). */
  fromBlock?: bigint;
  /**
   * The largest block range of one eth_getLogs request. Unset, a scan asks for the whole range at once and learns the
   * node's cap from its first refusal (public RPCs cap the range: publicnode 50 000, sepolia.base.org 200), so a cold
   * scan costs one request on a node that allows it.
   */
  logChunk?: bigint;
  /** How often receipts are polled, in ms (default 1000). */
  pollingInterval?: number;
}

/** The blocks scanned again at the next scan: a block replaced in a reorg loses its events and gets the new ones. */
const REORG_MARGIN = 8n;
/** A refused range is split down to this size before the error is given up on. */
const MIN_LOG_CHUNK = 10n;

interface LogCursor<T> {
  /** The first block the next scan reads. */
  next: bigint;
  /** The events found so far, by `tx:logIndex` (a block scanned twice adds nothing). */
  events: Map<string, { block: bigint; event: T }>;
}

type Found<T> = { id: string; block: bigint; event: T };
type LogRequest<T> = (fromBlock: bigint, toBlock: bigint) => Promise<Array<Found<T>>>;

/**
 * The block cap a node names in its refusal ("exceed maximum block range: 50000", "limited to a 200-block range",
 * "more than 10,000 results"): the first whole number of two or more digits that is not part of a version or a
 * decimal (viem appends "Version: viem@2.x" to its messages).
 */
export function rangeCapOf(message: string): bigint | null {
  const text = message.replace(/,(?=\d{3})/g, "").replace(/\bviem@\S+/gi, "");
  const m = /(?<![\d.@])(\d{2,})(?![\d.]*\.\d)/.exec(text);
  if (!m) return null;
  const n = BigInt(m[1]!);
  return n >= MIN_LOG_CHUNK ? n : null;
}

const KNOWN: ReadonlySet<string> = new Set<ChainErrorName>([
  "CapExceeded",
  "NonceUsed",
  "BadSignature",
  "NotService",
  "PayToNotService",
  "ActualAboveMax",
  "ZeroAddress",
  "ERC20InsufficientBalance",
  "ERC20InsufficientAllowance",
  "InsufficientMintAllowance",
  "ExchangeDisabled",
  "BadCredits",
  "OwnableUnauthorizedAccount",
  "HandleExists",
  "HandleTaken",
  "BadName",
  "BadUrl",
  "NameTaken",
  "NoHandle",
  "NotAServiceToken",
  "NotAuthorized",
  "UnknownToken",
  "BadHash",
  "UnknownEntry",
]);

/** Turns a viem revert into a ChainError with the contract's error name and arguments. */
/**
 * Every custom error of the four contracts, once by name: a revert that bubbles up from a token (an ERC-20 error
 * inside contract A's settle or exchange) decodes with the caller's ABI plus these.
 */
const ALL_ERRORS: readonly unknown[] = (() => {
  const seen = new Set<string>();
  const out: unknown[] = [];
  for (const abi of [
    serviceAuthorizationAbi,
    serviceTokenAbi,
    serviceDirectoryAbi,
    wpCreditAbi,
    extensionDirectoryAbi,
  ] as ReadonlyArray<ReadonlyArray<{ type: string; name?: string }>>) {
    for (const item of abi) {
      if (item.type !== "error" || !item.name || seen.has(item.name)) continue;
      seen.add(item.name);
      out.push(item);
    }
  }
  return out;
})();

function withErrors(abi: readonly unknown[]): readonly unknown[] {
  const names = new Set(
    (abi as ReadonlyArray<{ type: string; name?: string }>).filter((i) => i.type === "error").map((i) => i.name),
  );
  return [...abi, ...ALL_ERRORS.filter((e) => !names.has((e as { name: string }).name))];
}

export function toChainError(e: unknown): ChainError {
  if (e instanceof ChainError) return e;
  if (e instanceof BaseError) {
    const reverted = e.walk(
      (err) => err instanceof ContractFunctionRevertedError,
    ) as ContractFunctionRevertedError | null;
    let name = reverted?.data?.errorName;
    let args = (reverted?.data?.args ?? []) as unknown[];
    if (!name && reverted?.raw) {
      try {
        const d = decodeErrorResult({ abi: ALL_ERRORS as never, data: reverted.raw });
        name = d.errorName;
        args = (d.args ?? []) as unknown[];
      } catch {
        /* not one of ours */
      }
    }
    // the message names the error as the simulator does: CapExceeded(…), ERC20InsufficientBalance(…)
    if (name && KNOWN.has(name)) return new ChainError(name as ChainErrorName, args);
    if (/insufficient funds/i.test(e.shortMessage) || /insufficient funds/i.test(e.details ?? ""))
      return new ChainError("InsufficientFunds", [], e.shortMessage);
    // not one of ours: keep what the node said (the error's name, its selector, or the RPC message)
    const what = name ? `${name}(${args.map(String).join(", ")})` : (reverted?.signature ?? reverted?.raw ?? e.details);
    return new ChainError("Unknown", [], what ? `${e.shortMessage} ${what}` : e.shortMessage);
  }
  return new ChainError("Unknown", [], e instanceof Error ? e.message : String(e));
}

/** The ChainWriter on a JSON-RPC endpoint (Base Sepolia, anvil) with viem. */
export class ViemChain implements ChainWriter {
  readonly addresses: ChainAddresses;
  readonly pub: PublicClient;
  readonly wallet: WalletClient | null;
  readonly account: Account | null;
  private readonly chain: Chain;
  private readonly fromBlock: bigint;
  /** The largest range one eth_getLogs may ask for; null = unknown yet (the node's refusals lower it). */
  private logChunk: bigint | null;
  private readonly cursors = new Map<string, LogCursor<unknown>>();
  /** One scan at a time per cursor: two callers never interleave their reads of the same filter. */
  private readonly scans = new Map<string, Promise<unknown>>();
  private readonly abis = {
    a: serviceAuthorizationAbi,
    token: serviceTokenAbi,
    dir: serviceDirectoryAbi,
    wp: wpCreditAbi,
    xd: extensionDirectoryAbi,
  };

  constructor(cfg: ViemChainConfig) {
    this.addresses = cfg.addresses;
    this.chain = {
      id: cfg.addresses.chainId,
      name: `chain-${cfg.addresses.chainId}`,
      nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
      rpcUrls: { default: { http: [cfg.rpcUrl] } },
    };
    const transport = cfg.transport ?? http(cfg.rpcUrl);
    // receipts are polled every second (viem's default is four): Base blocks every two seconds, anvil mines at once
    this.pub = createPublicClient({ chain: this.chain, transport, pollingInterval: cfg.pollingInterval ?? 1_000 });
    this.account = cfg.account ?? null;
    this.wallet = cfg.account ? createWalletClient({ chain: this.chain, transport, account: cfg.account }) : null;
    this.fromBlock = cfg.fromBlock ?? 0n;
    this.logChunk = cfg.logChunk && cfg.logChunk > 0n ? cfg.logChunk : null;
  }

  private from(expected: Address): { wallet: WalletClient; account: Account } {
    if (!this.wallet || !this.account) throw new ChainError("Unknown", [], "this chain client holds no key");
    if (this.account.address.toLowerCase() !== expected.toLowerCase()) {
      throw new ChainError("Unknown", [], `the client's key is ${this.account.address}, not ${expected}`);
    }
    return { wallet: this.wallet, account: this.account };
  }

  private async write(
    expectedFrom: Address,
    req: { address: Address; abi: readonly unknown[]; functionName: string; args?: readonly unknown[]; value?: bigint },
  ): Promise<Tx> {
    const { receipt } = await this.writeReceipt(expectedFrom, req);
    return { tx: receipt.transactionHash, block: Number(receipt.blockNumber) };
  }

  /** A write with its receipt: the logs of the transaction answer what it did (a settle's Spent event). */
  private async writeReceipt(
    expectedFrom: Address,
    req: { address: Address; abi: readonly unknown[]; functionName: string; args?: readonly unknown[]; value?: bigint },
  ): Promise<{ receipt: TransactionReceipt }> {
    const { wallet, account } = this.from(expectedFrom);
    try {
      // simulate first so a revert surfaces with its name and arguments
      const sim = await this.pub.simulateContract({
        address: req.address,
        abi: withErrors(req.abi) as never,
        functionName: req.functionName as never,
        args: req.args as never,
        account,
        value: req.value,
      });
      const hash = await wallet.writeContract(sim.request as never);
      const receipt = await this.pub.waitForTransactionReceipt({ hash });
      if (receipt.status !== "success") throw new ChainError("Unknown", [], "the transaction reverted");
      return { receipt };
    } catch (e) {
      throw toChainError(e);
    }
  }

  private async read<T>(req: {
    address: Address;
    abi: readonly unknown[];
    functionName: string;
    args?: readonly unknown[];
  }): Promise<T> {
    try {
      return (await this.pub.readContract({
        address: req.address,
        abi: withErrors(req.abi) as never,
        functionName: req.functionName as never,
        args: req.args as never,
      })) as T;
    } catch (e) {
      throw toChainError(e);
    }
  }

  // ---- Views

  async authorizationOf(client: Address, identity: Address, token: Address): Promise<Authorization> {
    const r = await this.read<{ cap: bigint; used: bigint }>({
      address: this.addresses.contractA,
      abi: this.abis.a,
      functionName: "authorizationOf",
      args: [client, identity, token],
    });
    return { cap: r.cap, used: r.used };
  }

  async remaining(client: Address, identity: Address, token: Address): Promise<bigint> {
    return this.read<bigint>({
      address: this.addresses.contractA,
      abi: this.abis.a,
      functionName: "remaining",
      args: [client, identity, token],
    });
  }

  async tokensOf(client: Address, identity: Address): Promise<Address[]> {
    return [
      ...(await this.read<readonly Address[]>({
        address: this.addresses.contractA,
        abi: this.abis.a,
        functionName: "tokensOf",
        args: [client, identity],
      })),
    ];
  }

  async nonceUsed(identity: Address, nonce: Hex): Promise<boolean> {
    return this.read<boolean>({
      address: this.addresses.contractA,
      abi: this.abis.a,
      functionName: "nonceUsed",
      args: [identity, nonce],
    });
  }

  async balanceOf(token: Address, account: Address): Promise<bigint> {
    return this.read<bigint>({ address: token, abi: this.abis.token, functionName: "balanceOf", args: [account] });
  }

  async allowance(token: Address, owner: Address, spender: Address): Promise<bigint> {
    return this.read<bigint>({
      address: token,
      abi: this.abis.token,
      functionName: "allowance",
      args: [owner, spender],
    });
  }

  async tokenInfo(token: Address): Promise<TokenInfo> {
    const isWp = token.toLowerCase() === this.addresses.wpCredit.toLowerCase();
    const abi = isWp ? this.abis.wp : this.abis.token;
    const call = <T>(functionName: string) => this.read<T>({ address: token, abi, functionName });
    const [name, symbol, decimals, owner, totalSupply] = await Promise.all([
      call<string>("name"),
      call<string>("symbol"),
      call<number>("decimals"),
      call<Address>("owner"),
      call<bigint>("totalSupply"),
    ]);
    const [service, rate] = isWp
      ? ["0x0000000000000000000000000000000000000000" as Address, 0n]
      : await Promise.all([call<Address>("service"), call<bigint>("rate")]);
    return { address: getAddress(token), name, symbol, decimals: Number(decimals), owner, service, rate, totalSupply };
  }

  async mintAllowance(token: Address, minter: Address): Promise<bigint> {
    return this.read<bigint>({ address: token, abi: this.abis.token, functionName: "mintAllowance", args: [minter] });
  }

  async ethBalance(account: Address): Promise<bigint> {
    return this.pub.getBalance({ address: account });
  }

  async gasPrice(): Promise<bigint> {
    return this.pub.getGasPrice();
  }

  async blockNumber(): Promise<bigint> {
    // the live height, not viem's cached one (it caches for a second)
    return this.pub.getBlockNumber({ cacheTime: 0 });
  }

  async dirHandleOf(owner: Address) {
    const [name, addr4, exists] = await this.read<readonly [string, Hex, boolean]>({
      address: this.addresses.directory,
      abi: this.abis.dir,
      functionName: "handleOf",
      args: [owner],
    });
    return { name, addr4: addr4.slice(2).toLowerCase(), exists };
  }

  private entryFrom(e: {
    token: Address;
    owner: Address;
    handleName: string;
    addr4: Hex;
    serviceName: string;
    url: string;
    pricingHash: Hex;
    registeredAt: bigint;
    updatedAt: bigint;
    active: boolean;
  }): DirectoryEntry {
    return {
      token: e.token,
      owner: e.owner,
      handleName: e.handleName,
      addr4: e.addr4.slice(2).toLowerCase(),
      serviceName: e.serviceName,
      url: e.url,
      pricingHash: e.pricingHash,
      registeredAt: Number(e.registeredAt),
      updatedAt: Number(e.updatedAt),
      active: e.active,
    };
  }

  async dirEntry(token: Address): Promise<DirectoryEntry> {
    return this.entryFrom(
      await this.read({ address: this.addresses.directory, abi: this.abis.dir, functionName: "entry", args: [token] }),
    );
  }

  async dirTokensOf(owner: Address): Promise<Address[]> {
    return [
      ...(await this.read<readonly Address[]>({
        address: this.addresses.directory,
        abi: this.abis.dir,
        functionName: "tokensOf",
        args: [owner],
      })),
    ];
  }

  async dirCount(): Promise<number> {
    return Number(
      await this.read<bigint>({ address: this.addresses.directory, abi: this.abis.dir, functionName: "count" }),
    );
  }

  async dirEntryAt(index: number): Promise<DirectoryEntry> {
    return this.entryFrom(
      await this.read({
        address: this.addresses.directory,
        abi: this.abis.dir,
        functionName: "entryAt",
        args: [BigInt(index)],
      }),
    );
  }

  /** The ExtensionDirectory's address, or a plain error on a stack deployed before it existed. */
  private xd(): Address {
    const a = this.addresses.extensionDirectory;
    if (!a) throw new ChainError("Unknown", [], "this stack has no ExtensionDirectory");
    return a;
  }

  private extEntryFrom(e: {
    owner: Address;
    name: string;
    url: string;
    hash: Hex;
    updatedAt: bigint;
    active: boolean;
  }): ExtensionEntry {
    return {
      owner: e.owner,
      name: e.name,
      url: e.url,
      hash: e.hash.toLowerCase() as Hex,
      updatedAt: Number(e.updatedAt),
      active: e.active,
    };
  }

  async extEntry(owner: Address, name: string): Promise<ExtensionEntry> {
    return this.extEntryFrom(
      await this.read({ address: this.xd(), abi: this.abis.xd, functionName: "entry", args: [owner, name] }),
    );
  }

  async extEntriesOf(owner: Address): Promise<ExtensionEntry[]> {
    const list = await this.read<ReadonlyArray<Parameters<ViemChain["extEntryFrom"]>[0]>>({
      address: this.xd(),
      abi: this.abis.xd,
      functionName: "entriesOf",
      args: [owner],
    });
    return list.map((e) => this.extEntryFrom(e));
  }

  async extCount(): Promise<number> {
    return Number(await this.read<bigint>({ address: this.xd(), abi: this.abis.xd, functionName: "count" }));
  }

  async extEntryAt(index: number): Promise<ExtensionEntry> {
    return this.extEntryFrom(
      await this.read({ address: this.xd(), abi: this.abis.xd, functionName: "entryAt", args: [BigInt(index)] }),
    );
  }

  async dirResolve(handleName: string, addr4: string, serviceName: string): Promise<Address | null> {
    const t = await this.read<Address>({
      address: this.addresses.directory,
      abi: this.abis.dir,
      functionName: "resolve",
      args: [handleName, `0x${addr4}` as Hex, serviceName],
    });
    return t === "0x0000000000000000000000000000000000000000" ? null : t;
  }

  /**
   * Scans the logs of one filter and keeps a cursor per filter: the next scan starts a few blocks before where the
   * last one ended, not at FROM_BLOCK. The events found so far stay in memory by `tx:logIndex`; the blocks scanned
   * again drop their events first, so a block replaced in a reorg loses them and a block scanned twice adds nothing.
   * The scans of one filter run one at a time, and a scan builds its new map before it swaps it in, so two callers
   * never see a half-refilled list. The requests ask for the whole remaining range unless `logChunk` is set or a
   * node's refusal taught the client its cap (see `getLogsRange`).
   */
  private scanLogs<T>(key: string, request: LogRequest<T>): Promise<T[]> {
    const prev = this.scans.get(key) ?? Promise.resolve();
    const run = prev.then(
      () => this.scanOnce(key, request),
      () => this.scanOnce(key, request),
    );
    this.scans.set(key, run);
    void run.finally(() => {
      if (this.scans.get(key) === run) this.scans.delete(key);
    });
    return run;
  }

  private async scanOnce<T>(key: string, request: LogRequest<T>): Promise<T[]> {
    let cursor = this.cursors.get(key) as LogCursor<T> | undefined;
    if (!cursor) {
      cursor = { next: this.fromBlock, events: new Map() };
      this.cursors.set(key, cursor as LogCursor<unknown>);
    }
    const latest = await this.blockNumber();
    const start = cursor.next;
    const next = new Map([...cursor.events].filter(([, e]) => e.block < start));
    if (start <= latest) for (const f of await this.getLogsRange(request, start, latest)) next.set(f.id, f);
    const rescanFrom = latest + 1n - REORG_MARGIN;
    cursor.events = next;
    cursor.next = rescanFrom > start ? rescanFrom : start;
    return [...next.values()].sort((a, b) => (a.block < b.block ? -1 : a.block > b.block ? 1 : 0)).map((e) => e.event);
  }

  /**
   * eth_getLogs over [from, to] in requests of at most `logChunk` blocks. A refused range lowers the chunk: to the
   * cap the node names in its message, else to half the range, down to MIN_LOG_CHUNK; the client keeps the lowered
   * chunk for its later scans. Any other error, or a refusal below the minimum, is thrown.
   */
  private async getLogsRange<T>(request: LogRequest<T>, from: bigint, to: bigint): Promise<Array<Found<T>>> {
    const out: Array<Found<T>> = [];
    let cursor = from;
    while (cursor <= to) {
      const size = this.logChunk === null ? to - cursor + 1n : this.logChunk;
      const end = cursor + size - 1n < to ? cursor + size - 1n : to;
      try {
        out.push(...(await request(cursor, end)));
        cursor = end + 1n;
      } catch (e) {
        const span = end - cursor + 1n;
        if (span <= MIN_LOG_CHUNK) throw e;
        // the node's own words: viem's details or short message, not its composed message with a version line
        const text = e instanceof BaseError ? e.details || e.shortMessage : e instanceof Error ? e.message : String(e);
        const named = rangeCapOf(text);
        const lowered = named !== null && named < span ? named : span / 2n;
        this.logChunk = lowered > MIN_LOG_CHUNK ? lowered : MIN_LOG_CHUNK;
      }
    }
    return out;
  }

  /** A one-off scan over [fromBlock, head], no cursor (the lookup of one settle by its nonce). */
  private async scanFrom<T>(fromBlock: bigint, request: LogRequest<T>): Promise<T[]> {
    const latest = await this.blockNumber();
    const from = fromBlock > this.fromBlock ? fromBlock : this.fromBlock;
    if (from > latest) return [];
    return (await this.getLogsRange(request, from, latest)).map((f) => f.event);
  }

  private spentOf(logs: Parameters<typeof parseEventLogs>[0]["logs"]): Array<Found<SpentEvent>> {
    return parseEventLogs({ abi: this.abis.a, eventName: "Spent", logs }).map((l) => ({
      id: `${l.transactionHash}:${l.logIndex}`,
      block: l.blockNumber,
      event: {
        client: l.args.client,
        identity: l.args.identity,
        token: l.args.token,
        payTo: l.args.payTo,
        actual: l.args.actual,
        nonce: l.args.nonce,
        credits: l.args.credits,
        tx: l.transactionHash,
        block: Number(l.blockNumber),
      },
    }));
  }

  async spentEvents(
    filter: {
      token?: Address;
      client?: Address;
      identity?: Address;
      payTo?: Address;
    },
    opts: ScanOptions = {},
  ): Promise<SpentEvent[]> {
    const lower = (a?: Address) => a?.toLowerCase() ?? "*";
    const request: LogRequest<SpentEvent> = async (fromBlock, toBlock) =>
      this.spentOf(
        await this.pub.getLogs({
          address: this.addresses.contractA,
          event: this.abis.a.find((x) => x.type === "event" && x.name === "Spent") as never,
          args: { client: filter.client, identity: filter.identity, token: filter.token } as never,
          fromBlock,
          toBlock,
        }),
      );
    const events =
      opts.fromBlock !== undefined
        ? await this.scanFrom(opts.fromBlock, request)
        : await this.scanLogs(
            `spent|${lower(filter.client)}|${lower(filter.identity)}|${lower(filter.token)}`,
            request,
          );
    return events.filter((e) => !filter.payTo || e.payTo.toLowerCase() === filter.payTo.toLowerCase());
  }

  async exchangedEvents(token: Address): Promise<ExchangedEvent[]> {
    return this.scanLogs<ExchangedEvent>(`exchanged|${token.toLowerCase()}`, async (fromBlock, toBlock) => {
      const logs = await this.pub.getLogs({
        address: token,
        event: this.abis.token.find((x) => x.type === "event" && x.name === "Exchanged") as never,
        fromBlock,
        toBlock,
      });
      return parseEventLogs({ abi: this.abis.token, eventName: "Exchanged", logs }).map((l) => ({
        id: `${l.transactionHash}:${l.logIndex}`,
        block: l.blockNumber,
        event: {
          token,
          from: l.args.from,
          to: l.args.to,
          credits: l.args.credits,
          tokens: l.args.tokens,
          tx: l.transactionHash,
        },
      }));
    });
  }

  // ---- Writes

  async authorize(from: Address, identity: Address, token: Address, cap: bigint): Promise<Tx> {
    return this.write(from, {
      address: this.addresses.contractA,
      abi: this.abis.a,
      functionName: "authorize",
      args: [identity, token, cap],
    });
  }

  async multicallAuthorize(
    from: Address,
    calls: Array<{ identity: Address; token: Address; cap: bigint }>,
  ): Promise<Tx> {
    const data = calls.map((c) =>
      encodeFunctionData({ abi: this.abis.a, functionName: "authorize", args: [c.identity, c.token, c.cap] }),
    );
    return this.write(from, {
      address: this.addresses.contractA,
      abi: this.abis.a,
      functionName: "multicall",
      args: [data],
    });
  }

  /** The settle with the credits of its own Spent event, read from the receipt: no scan of the chain. */
  async settle(from: Address, intent: Intent, signature: Hex, actual: bigint): Promise<SettleResult> {
    const { receipt } = await this.writeReceipt(from, {
      address: this.addresses.contractA,
      abi: this.abis.a,
      functionName: "settle",
      args: [intent, signature, actual],
    });
    let credits = 0n;
    try {
      const spent = this.spentOf(receipt.logs).find(
        (f) =>
          f.event.nonce.toLowerCase() === intent.nonce.toLowerCase() &&
          f.event.identity.toLowerCase() === intent.identity.toLowerCase(),
      );
      credits = spent?.event.credits ?? 0n;
    } catch {
      credits = 0n;
    }
    return { tx: receipt.transactionHash, block: Number(receipt.blockNumber), credits };
  }

  async approve(from: Address, token: Address, spender: Address, amount: bigint): Promise<Tx> {
    return this.write(from, { address: token, abi: this.abis.token, functionName: "approve", args: [spender, amount] });
  }

  async transfer(from: Address, token: Address, to: Address, amount: bigint): Promise<Tx> {
    return this.write(from, { address: token, abi: this.abis.token, functionName: "transfer", args: [to, amount] });
  }

  async exchange(from: Address, token: Address, credits: bigint, to: Address): Promise<Tx & { tokens: bigint }> {
    const tx = await this.write(from, {
      address: token,
      abi: this.abis.token,
      functionName: "exchange",
      args: [credits, to],
    });
    const rate = await this.read<bigint>({ address: token, abi: this.abis.token, functionName: "rate" });
    return { ...tx, tokens: rate > 0n ? credits / rate : 0n };
  }

  async mint(from: Address, token: Address, to: Address, amount: bigint): Promise<Tx> {
    return this.write(from, { address: token, abi: this.abis.token, functionName: "mint", args: [to, amount] });
  }

  async setRate(from: Address, token: Address, rate: bigint): Promise<Tx> {
    return this.write(from, { address: token, abi: this.abis.token, functionName: "setRate", args: [rate] });
  }

  async setService(from: Address, token: Address, payTo: Address): Promise<Tx> {
    return this.write(from, { address: token, abi: this.abis.token, functionName: "setService", args: [payTo] });
  }

  async setMintAllowance(from: Address, token: Address, minter: Address, allowance: bigint): Promise<Tx> {
    return this.write(from, {
      address: token,
      abi: this.abis.token,
      functionName: "setMintAllowance",
      args: [minter, allowance],
    });
  }

  async deployToken(from: Address, name: string, symbol: string, rate: bigint): Promise<Tx & { address: Address }> {
    const { wallet, account } = this.from(from);
    try {
      const hash = await wallet.deployContract({
        abi: this.abis.token,
        bytecode: serviceTokenBytecode as Hex,
        args: [name, symbol, from, this.addresses.wpCredit, rate],
        account,
        chain: this.chain,
      });
      const receipt = await this.pub.waitForTransactionReceipt({ hash });
      if (receipt.status !== "success" || !receipt.contractAddress)
        throw new ChainError("Unknown", [], "the deployment reverted");
      return { tx: hash, block: Number(receipt.blockNumber), address: getAddress(receipt.contractAddress) };
    } catch (e) {
      throw toChainError(e);
    }
  }

  async claim(from: Address, name: string): Promise<Tx> {
    return this.write(from, {
      address: this.addresses.directory,
      abi: this.abis.dir,
      functionName: "claim",
      args: [name],
    });
  }

  async set(from: Address, token: Address, serviceName: string, url: string): Promise<Tx> {
    return this.write(from, {
      address: this.addresses.directory,
      abi: this.abis.dir,
      functionName: "set",
      args: [token, serviceName, url],
    });
  }

  async setPricingHash(from: Address, token: Address, pricingHash: Hex): Promise<Tx> {
    return this.write(from, {
      address: this.addresses.directory,
      abi: this.abis.dir,
      functionName: "setPricingHash",
      args: [token, pricingHash],
    });
  }

  async clear(from: Address, token: Address): Promise<Tx> {
    return this.write(from, {
      address: this.addresses.directory,
      abi: this.abis.dir,
      functionName: "clear",
      args: [token],
    });
  }

  async publish(from: Address, name: string, url: string, hash: Hex): Promise<Tx> {
    return this.write(from, {
      address: this.xd(),
      abi: this.abis.xd,
      functionName: "publish",
      args: [name, url, hash],
    });
  }

  async unpublish(from: Address, name: string): Promise<Tx> {
    return this.write(from, { address: this.xd(), abi: this.abis.xd, functionName: "unpublish", args: [name] });
  }

  async sendEth(from: Address, to: Address, amount: bigint): Promise<Tx> {
    const { wallet, account } = this.from(from);
    try {
      const hash = await wallet.sendTransaction({ account, to, value: amount, chain: this.chain });
      const receipt = await this.pub.waitForTransactionReceipt({ hash });
      return { tx: hash, block: Number(receipt.blockNumber) };
    } catch (e) {
      throw toChainError(e);
    }
  }

  async wpMint(from: Address, to: Address, amount: bigint): Promise<Tx> {
    return this.write(from, {
      address: this.addresses.wpCredit,
      abi: this.abis.wp,
      functionName: "mint",
      args: [to, amount],
    });
  }
}
